import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { inputsMaxLengths } from '../../constants';
import styles from './ToggleTreatmentModal.module.scss';
import { Button, Input, Modal, RadioButton, Text, Textarea } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { TaskBlockReason } from 'types';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

const options = [
  ['Referred to Ortho', TaskBlockReason.REFFERED],
  ['Refused', TaskBlockReason.REFUSED],
  ['Recall', TaskBlockReason.RECALL],
  ['Retainer', TaskBlockReason.RETAINER],
  ['Pending', TaskBlockReason.PENDING],
  ['Dental Tx required', TaskBlockReason.DENTAL_TX_REQUIRED],
  ['Other', TaskBlockReason.OTHER],
] as const;

const scheme = yup.object({
  reason: yup.string().required('Choose reason'),
  customReason: yup.string().when('reason', {
    is: TaskBlockReason.OTHER,
    then: (schema) =>
      schema
        .required('The field is empty')
        .matches(onlyLatinLettersSymbolsNumbers, {
          message: 'Only Latin Alphabet is allowed',
        })
        .max(inputsMaxLengths.outOfTreatmentReason),
  }),
});

export const ToggleTreatmentModal = () => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const isOutOfTreatment = !!currentPatientCase?.isTaskBlocked;

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(scheme),
    defaultValues: {
      reason: currentPatientCase?.taskBlockReason ?? '',
      customReason: currentPatientCase?.taskBlockComment ?? '',
    },
  });

  const currentOption = watch('reason') as TaskBlockReason;
  const customReason = watch('customReason');
  const isInputCustomReasonIsActive = currentOption === TaskBlockReason.OTHER;

  const onCloseHandler = () => {
    dispatch(appActions.setIsModalOpened({ name: 'treatmentModal', isOpened: false }));
  };

  const onReturnToTreatmentHandler = async () => {
    if (!currentPatientCase || isLoading) return;
    try {
      setIsLoading(true);
      await dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: {
            isTaskBlocked: false,
            taskBlockComment: null,
          },
          patientCaseId: currentPatientCase.id,
        }),
      ).unwrap();

      onCloseHandler();
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const onOutOfTreatmentHandler = handleSubmit(async () => {
    if (!currentPatientCase || isLoading) return;
    try {
      setIsLoading(true);
      await dispatch(
        patientsActions.updatePatientCase({
          patientCaseData:
            currentOption === TaskBlockReason.OTHER
              ? {
                  isTaskBlocked: true,
                  taskBlockReason: currentOption,
                  taskBlockComment: customReason,
                }
              : {
                  isTaskBlocked: true,
                  taskBlockReason: currentOption,
                },
          patientCaseId: currentPatientCase.id,
        }),
      ).unwrap();

      onCloseHandler();
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <Modal title={isOutOfTreatment ? 'Return form Out of treatment' : 'Out of treatment'} onClose={onCloseHandler}>
      {isOutOfTreatment ? (
        <>
          <div className={styles.backToTreatmentContent}>
            <Text type="body-big">Do you want to return the patient from the Out of treatment stage?</Text>
            <Text type="body2" className={styles.description}>
              The patient will be returned from the Out of treatment stage to the last stage and task it was
            </Text>
          </div>
          <div className={styles.buttonRow}>
            <Button isLink onClick={onCloseHandler}>
              Cancel
            </Button>
            <Button onClick={onReturnToTreatmentHandler}>Return</Button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}>
          <div className={styles.outOfTreatmentContent}>
            <Text type="body-big">Choose a reason for the out of treatment submission</Text>
            <div className={styles.chooseReason}>
              {options.map(([name, enumValue], index) =>
                index !== options.length - 1 ? (
                  <label htmlFor={name} key={name}>
                    <RadioButton
                      value={enumValue}
                      id={name}
                      register={register}
                      inputName="reason"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                    />

                    <Text type="body2">{name}</Text>
                  </label>
                ) : (
                  <div key={name}>
                    <label htmlFor={name}>
                      <RadioButton
                        value={enumValue}
                        id={name}
                        register={register}
                        inputName="reason"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <Text type="body2">{name}</Text>
                    </label>
                    <Textarea
                      labelText=""
                      className={styles.textInput}
                      inputName="customReason"
                      register={register}
                      autoComplete="off"
                      placeholder="Enter custom reason"
                      maxLength={inputsMaxLengths.outOfTreatmentReason}
                      setValue={setValue}
                      disabled={!isInputCustomReasonIsActive}
                      errorMsg={errors.reason?.message || errors.customReason?.message}
                    />
                  </div>
                ),
              )}
            </div>
          </div>
          <div className={styles.buttonRow}>
            <Button isLink onClick={onCloseHandler}>
              Cancel
            </Button>
            <Button onClick={onOutOfTreatmentHandler}>Save Changes</Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
